import { Api } from '@/api/api';
import { HttpClient } from '@/api/generatedApi';
import { getAuthToken } from './auth/getAuthToken';

export const getBaseUrl = () => (import.meta.env.DEV ? `/v3` : `${import.meta.env.VITE_API_BASE_URL}/v3`);

export const getApiDocumentationUrl = () => `${getBaseUrl()}/docs`;

let isRedirectInProgress = false;

const createHttpClient = (): HttpClient => {
  const client = new HttpClient({
    baseURL: getBaseUrl(),
    secure: true,
    withCredentials: true,
    securityWorker: async () => {
      const authToken = await getAuthToken();
      return {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    },
  });

  if (client.instance.interceptors?.response) {
    client.instance.interceptors.response.use(
      response => response,
      async error => {
        const skipRedirect = error.config?.meta?.skipRedirect;
        if (error.response && !isRedirectInProgress && !skipRedirect) {
          if (error.response.status === 401) {
            isRedirectInProgress = true;
            localStorage.removeItem('authToken');
            const isOnExceptionPage = redirectExceptions.some(path => globalThis.location.pathname.startsWith(path));
            if (isOnExceptionPage) {
              isRedirectInProgress = false;
            } else if (!globalThis.location.pathname.startsWith('/signin')) {
              globalThis.location.assign('/signin');
            }
          } else {
            isRedirectInProgress = false;
          }
        }
        throw error;
      }
    );
  }

  return client;
};

const redirectExceptions = ['/access-denied', '/oauth2/redirect', '/oauth2/signout'];

const resetRedirectFlag = () => {
  isRedirectInProgress = false;
};

interface GlobalWithEventListeners {
  addEventListener: (type: string, listener: () => void) => void;
}

const hasEventListeners = (globalObject: unknown): globalObject is GlobalWithEventListeners => {
  return typeof globalObject === 'object' && globalObject !== null && 'addEventListener' in globalObject;
};

if (hasEventListeners(globalThis)) {
  globalThis.addEventListener('beforeunload', resetRedirectFlag);
  globalThis.addEventListener('popstate', resetRedirectFlag);
}

export const api = new Proxy({} as Api, {
  get: (_target, property) => {
    const client: HttpClient = createHttpClient();
    const freshApi: Api = new Api(client);
    return freshApi[property as keyof Api];
  },
});
